import { supabase } from './supabase-client';
import { userService } from './user-service';
import { languageService } from './language-service';
import React from 'react';
import ReactDOM from 'react-dom/client';
import TermsDialog from '../components/TermsDialog';

/**
 * Gets localized terms of service based on language
 * @param {string} language - ISO language code
 * @returns {string} HTML formatted terms of service
 */
const getLocalizedTerms = (language) => {
  const terms = {
    // English (default)
    en: `
      <h2>Terms of Service</h2>
      <p>Welcome to CreativeAI! By using this service, you agree to the following terms:</p>
      <ul>
        <li>This is an educational tool designed for learning purposes only.</li>
        <li>We collect basic usage data to improve the service.</li>
        <li>We do not collect any personally identifiable information.</li>
        <li>You agree to use this tool responsibly and for educational purposes only.</li>
        <li>You must be 13 years or older to use this service.</li>
      </ul>
      <p>If you have any questions about these terms, please contact us.</p>
    `,

    // Danish
    da: `
      <h2>Servicevilkår</h2>
      <p>Velkommen til CreativeAI! Når du bruger denne tjeneste, accepterer du følgende vilkår:</p>
      <ul>
        <li>Dette er et undervisningsværktøj, der kun er beregnet til læring.</li>
        <li>Vi indsamler grundlæggende brugsdata for at forbedre tjenesten.</li>
        <li>Vi indsamler ikke personligt identificerbare oplysninger.</li>
        <li>Du accepterer at bruge dette værktøj ansvarligt og kun til undervisningsformål.</li>
        <li>Du skal være 13 år eller ældre for at bruge denne tjeneste.</li>
      </ul>
      <p>Kontakt os, hvis du har spørgsmål om disse vilkår.</p>
    `,
    
    // Spanish
    es: `
      <h2>Términos de Servicio</h2>
      <p>¡Bienvenido a CreativeAI! Al utilizar este servicio, aceptas los siguientes términos:</p>
      <ul>
        <li>Esta es una herramienta educativa diseñada solo para fines de aprendizaje.</li>
        <li>Recopilamos datos básicos de uso para mejorar el servicio.</li>
        <li>No recopilamos información de identificación personal.</li>
        <li>Aceptas usar esta herramienta de manera responsable y solo con fines educativos.</li>
        <li>Debes tener 13 años o más para usar este servicio.</li>
      </ul>
      <p>Si tienes alguna pregunta sobre estos términos, contáctanos.</p>
    `,
    
    // Japanese
    ja: `
      <h2>利用規約</h2>
      <p>CreativeAIへようこそ！このサービスを利用することにより、以下の条件に同意したことになります：</p>
      <ul>
        <li>これは学習目的のみを目的とした教育ツールです。</li>
        <li>サービス向上のために基本的な使用データを収集します。</li>
        <li>個人を特定できる情報は収集しません。</li>
        <li>このツールを責任を持って教育目的でのみ使用することに同意します。</li>
        <li>このサービスを利用するには13歳以上である必要があります。</li>
      </ul>
      <p>これらの条件について質問がある場合は、お問い合わせください。</p>
    `,
    
    // French
    fr: `
      <h2>Conditions d'utilisation</h2>
      <p>Bienvenue sur CreativeAI ! En utilisant ce service, vous acceptez les conditions suivantes :</p>
      <ul>
        <li>Ceci est un outil éducatif conçu uniquement à des fins d'apprentissage.</li>
        <li>Nous collectons des données d'utilisation de base pour améliorer le service.</li>
        <li>Nous ne collectons aucune information d'identification personnelle.</li>
        <li>Vous acceptez d'utiliser cet outil de manière responsable et uniquement à des fins éducatives.</li>
        <li>Vous devez avoir au moins 13 ans pour utiliser ce service.</li>
      </ul>
      <p>Si vous avez des questions sur ces conditions, veuillez nous contacter.</p>
    `,
    
    // Italian
    it: `
      <h2>Termini di Servizio</h2>
      <p>Benvenuto su CreativeAI! Utilizzando questo servizio, accetti i seguenti termini:</p>
      <ul>
        <li>Questo è uno strumento educativo progettato solo per scopi di apprendimento.</li>
        <li>Raccogliamo dati di utilizzo di base per migliorare il servizio.</li>
        <li>Non raccogliamo informazioni di identificazione personale.</li>
        <li>Accetti di utilizzare questo strumento in modo responsabile e solo per scopi educativi.</li>
        <li>Devi avere almeno 13 anni per utilizzare questo servizio.</li>
      </ul>
      <p>Se hai domande su questi termini, contattaci.</p>
    `,
    
    // German
    de: `
      <h2>Nutzungsbedingungen</h2>
      <p>Willkommen bei CreativeAI! Durch die Nutzung dieses Dienstes stimmst du den folgenden Bedingungen zu:</p>
      <ul>
        <li>Dies ist ein Bildungstool, das nur für Lernzwecke konzipiert ist.</li>
        <li>Wir sammeln grundlegende Nutzungsdaten, um den Dienst zu verbessern.</li>
        <li>Wir sammeln keine persönlich identifizierbaren Informationen.</li>
        <li>Du stimmst zu, dieses Tool verantwortungsvoll und nur für Bildungszwecke zu nutzen.</li>
        <li>Du musst mindestens 13 Jahre alt sein, um diesen Dienst zu nutzen.</li>
      </ul>
      <p>Wenn du Fragen zu diesen Bedingungen hast, kontaktiere uns bitte.</p>
    `,
    
    // Romanian
    ro: `
      <h2>Termeni de Utilizare</h2>
      <p>Bine ai venit la CreativeAI! Prin utilizarea acestui serviciu, ești de acord cu următorii termeni:</p>
      <ul>
        <li>Acesta este un instrument educațional conceput doar în scopuri de învățare.</li>
        <li>Colectăm date de bază despre utilizare pentru a îmbunătăți serviciul.</li>
        <li>Nu colectăm informații de identificare personală.</li>
        <li>Ești de acord să utilizezi acest instrument în mod responsabil și doar în scopuri educaționale.</li>
        <li>Trebuie să ai cel puțin 13 ani pentru a utiliza acest serviciu.</li>
      </ul>
      <p>Dacă ai întrebări despre acești termeni, te rugăm să ne contactezi.</p>
    `,
    
    // Portuguese
    pt: `
      <h2>Termos de Serviço</h2>
      <p>Bem-vindo ao CreativeAI! Ao usar este serviço, você concorda com os seguintes termos:</p>
      <ul>
        <li>Esta é uma ferramenta educacional projetada apenas para fins de aprendizado.</li>
        <li>Coletamos dados básicos de uso para melhorar o serviço.</li>
        <li>Não coletamos informações de identificação pessoal.</li>
        <li>Você concorda em usar esta ferramenta de forma responsável e apenas para fins educacionais.</li>
        <li>Você deve ter 13 anos ou mais para usar este serviço.</li>
      </ul>
      <p>Se você tiver alguma dúvida sobre estes termos, entre em contato conosco.</p>
    `,
    
    // Russian
    ru: `
      <h2>Условия использования</h2>
      <p>Добро пожаловать в CreativeAI! Используя этот сервис, вы соглашаетесь со следующими условиями:</p>
      <ul>
        <li>Это образовательный инструмент, предназначенный только для целей обучения.</li>
        <li>Мы собираем основные данные об использовании для улучшения сервиса.</li>
        <li>Мы не собираем личную идентифицирующую информацию.</li>
        <li>Вы соглашаетесь использовать этот инструмент ответственно и только в образовательных целях.</li>
        <li>Вам должно быть не менее 13 лет для использования этого сервиса.</li>
      </ul>
      <p>Если у вас есть вопросы об этих условиях, пожалуйста, свяжитесь с нами.</p>
    `,
    
    // Korean
    ko: `
      <h2>서비스 이용약관</h2>
      <p>CreativeAI에 오신 것을 환영합니다! 이 서비스를 사용함으로써 다음 약관에 동의하게 됩니다:</p>
      <ul>
        <li>이것은 학습 목적으로만 설계된 교육 도구입니다.</li>
        <li>서비스 개선을 위해 기본 사용 데이터를 수집합니다.</li>
        <li>개인 식별 정보는 수집하지 않습니다.</li>
        <li>이 도구를 책임감 있게, 교육 목적으로만 사용하는 데 동의합니다.</li>
        <li>이 서비스를 사용하려면 13세 이상이어야 합니다.</li>
      </ul>
      <p>이 약관에 대해 질문이 있으시면 저희에게 연락해 주세요.</p>
    `,
    
    // Chinese
    zh: `
      <h2>服务条款</h2>
      <p>欢迎使用 CreativeAI！使用本服务，即表示您同意以下条款：</p>
      <ul>
        <li>这是一个仅为学习目的而设计的教育工具。</li>
        <li>我们收集基本使用数据以改进服务。</li>
        <li>我们不收集任何个人身份信息。</li>
        <li>您同意负责任地使用此工具，且仅用于教育目的。</li>
        <li>您必须年满13岁才能使用本服务。</li>
      </ul>
      <p>如果您对这些条款有任何疑问，请与我们联系。</p>
    `,
    
    // Arabic
    ar: `
      <h2>شروط الخدمة</h2>
      <p>مرحبًا بك في CreativeAI! باستخدام هذه الخدمة، فإنك توافق على الشروط التالية:</p>
      <ul>
        <li>هذه أداة تعليمية مصممة لأغراض التعلم فقط.</li>
        <li>نقوم بجمع بيانات الاستخدام الأساسية لتحسين الخدمة.</li>
        <li>نحن لا نجمع أي معلومات تعريف شخصية.</li>
        <li>أنت توافق على استخدام هذه الأداة بمسؤولية ولأغراض تعليمية فقط.</li>
        <li>يجب أن يكون عمرك 13 عامًا أو أكثر لاستخدام هذه الخدمة.</li>
      </ul>
      <p>إذا كانت لديك أي أسئلة حول هذه الشروط، يرجى الاتصال بنا.</p>
    `,
    
    // Hindi
    hi: `
      <h2>सेवा की शर्तें</h2>
      <p>CreativeAI में आपका स्वागत है! इस सेवा का उपयोग करके, आप निम्नलिखित शर्तों से सहमत होते हैं:</p>
      <ul>
        <li>यह एक शैक्षिक उपकरण है जो केवल सीखने के उद्देश्यों के लिए डिज़ाइन किया गया है।</li>
        <li>हम सेवा को बेहतर बनाने के लिए बुनियादी उपयोग डेटा एकत्र करते हैं।</li>
        <li>हम किसी भी व्यक्तिगत पहचान योग्य जानकारी को एकत्र नहीं करते हैं।</li>
        <li>आप इस उपकरण का जिम्मेदारी से और केवल शैक्षिक उद्देश्यों के लिए उपयोग करने के लिए सहमत हैं।</li>
        <li>इस सेवा का उपयोग करने के लिए आपकी आयु 13 वर्ष या उससे अधिक होनी चाहिए।</li>
      </ul>
      <p>यदि आपके पास इन शर्तों के बारे में कोई प्रश्न हैं, तो कृपया हमसे संपर्क करें।</p>
    `
  };
  
  return terms[language] || terms.en;
};

/**
 * Service to handle user consent and terms of service
 */
const consentService = {
  /**
   * Check if user has previously consented
   * @returns {boolean} Whether user has consented
   */
  hasConsented() {
    const consent = localStorage.getItem('cognimates_copilot_consent');
    return consent === 'true';
  },
  
  /**
   * Reset consent (for testing purposes)
   */
  resetConsent() {
    localStorage.removeItem('cognimates_copilot_consent');
  },
  
  /**
   * Save user consent and log to Supabase
   * @param {boolean} consented - Whether user consented
   * @returns {boolean} The consent value
   */
  async saveConsent(consented) {
    // Always save to localStorage for local state management
    localStorage.setItem('cognimates_copilot_consent', consented ? 'true' : 'false');
    
    // Log consent to Supabase for compliance records (if available)
    if (supabase) {
      try {
        const userId = userService.getUserId();
        const language = languageService.getLanguage();
        
        const { error } = await supabase
          .from('user_consent')
          .upsert({
            user_id: userId,
            language: language,
            consented: consented,
            timestamp: new Date().toISOString()
          }, { onConflict: 'user_id' });
        
        if (error) {
          console.error('Error logging consent to Supabase:', error);
          // Continue with local consent even if Supabase fails
        } else {
          console.log('Successfully logged consent to Supabase');
        }
      } catch (error) {
        console.error('Failed to record consent to Supabase:', error);
        // Continue with local consent even if Supabase fails
      }
    } else {
      console.warn('Supabase client not initialized. Using local consent storage only.');
    }
    
    return consented;
  },
  
  /**
   * Show consent dialog to user with child-friendly terms
   * @param {boolean} forceShow - Force showing the dialog even if consent already given
   * @returns {Promise<boolean>} Whether consent was given
   */
  async requestInitialConsent(forceShow = false) {
    // For development/testing, uncomment to reset consent
    // this.resetConsent();
    
    // Check localStorage first to avoid showing dialog if already consented
    if (!forceShow && this.hasConsented()) {
      console.log('User has already consented, not showing dialog');
      return true;
    }
    
    const language = languageService.getLanguage();
    
    // Static variable to track if dialog is already showing
    if (this.isDialogShowing) {
      console.log('Dialog is already showing, not creating a second one');
      return new Promise(resolve => {
        // Add a listener for when consent is saved
        const checkInterval = setInterval(() => {
          if (this.hasConsented()) {
            clearInterval(checkInterval);
            resolve(true);
          }
        }, 500);
      });
    }
    
    this.isDialogShowing = true;
    
    // Show localized terms of service using the new component
    return new Promise((resolve) => {
      // Create a container for the dialog
      const dialogContainer = document.createElement('div');
      dialogContainer.id = 'terms-dialog-container';
      document.body.appendChild(dialogContainer);
      
      // Function to remove the dialog
      const removeDialog = () => {
        root.unmount();
        document.body.removeChild(dialogContainer);
        this.isDialogShowing = false;
      };
      
      // Handle accept/decline actions
      const handleAccept = async () => {
        removeDialog();
        const result = await this.saveConsent(true);
        resolve(result);
      };
      
      const handleDecline = async () => {
        removeDialog();
        const result = await this.saveConsent(false);
        resolve(result);
      };
      
      // Create root and render the dialog using modern React API
      const root = ReactDOM.createRoot(dialogContainer);
      root.render(
        <TermsDialog
          termsText={getLocalizedTerms(language)}
          onAccept={handleAccept}
          onDecline={handleDecline}
          language={language}
        />
      );
    });
  },
  
  /**
   * Delete all user data (GDPR/COPPA compliance)
   * @returns {Promise<boolean>} Whether deletion was successful
   */
  async deleteAllUserData() {
    try {
      const userId = userService.getUserId();
      const language = languageService.getLanguage();
      
      // Localize deletion confirmation based on language
      const confirmMessages = {
        en: 'Are you sure you want to delete all your data? This cannot be undone.',
        es: '¿Estás seguro de que deseas eliminar todos tus datos? Esto no se puede deshacer.',
        ja: '本当にすべてのデータを削除しますか？この操作は元に戻せません。',
        fr: 'Êtes-vous sûr de vouloir supprimer toutes vos données ? Cette action ne peut pas être annulée.',
        da: 'Er du sikker på, at du vil slette alle dine data? Dette kan ikke fortrydes.'
      };
      
      const message = confirmMessages[language] || confirmMessages.en;
      
      // Confirm deletion with user
      if (!window.confirm(message)) {
        return false;
      }
      
      // Delete user data from all tables
      const tables = ['user_interactions', 'user_consent', 'user_sessions'];
      
      for (const table of tables) {
        const { error } = await supabase
          .from(table)
          .delete()
          .eq('user_id', userId);
          
        if (error) {
          console.error(`Error deleting from ${table}:`, error);
          return false;
        }
      }
      
      // Clear local storage
      localStorage.removeItem('cognimates_copilot_consent');
      localStorage.removeItem('cognimates_copilot_user_id');
      
      // Generate a new user ID
      userService.generateUserId();
      
      return true;
    } catch (error) {
      console.error('Failed to delete user data:', error);
      return false;
    }
  }
};

// Initialize the static property
consentService.isDialogShowing = false;

export default consentService;
